import { useCallback, useEffect, useRef, useState } from "react";

import { type TourPersistence } from "./tourPersistence";

type UseGuidedTourArgs = {
  tourId: string;
  totalSteps: number;
  persistence: TourPersistence;
  /**
   * Skip the auto-trigger on mount even when the tour hasn't been
   * completed. Used by the "Take tour again" entry-point in Account
   * — there the customer is choosing to launch the tour with a
   * deliberate click on a different screen; we don't want a second
   * auto-fire on the next /home visit since they just saw it.
   */
  disableAutoTrigger?: boolean;
};

export type UseGuidedTourResult = {
  active: boolean;
  currentStep: number;
  start: () => void;
  close: () => void;
  next: () => void;
  prev: () => void;
  reset: () => Promise<void>;
};

// Wait one tick after mount before auto-triggering so the sidebar
// has rendered and the target elements are measurable. 600ms
// dodges the LayoutShift Intercom + other third-party scripts
// inject on first paint.
const AUTO_TRIGGER_DELAY_MS = 600;

// Helper to fire-and-forget a persistence write while still logging
// failures. Kept at module scope so the call sites stay short and
// the `void promise` pattern (forbidden by Sonar Way's S3735)
// doesn't reappear.
const reportIfFailed = (
  op: Promise<void>,
  label: string,
  tourId: string,
): void => {
  op.catch((err) => {
    // eslint-disable-next-line no-console
    console.warn(`[useGuidedTour] ${label} failed for ${tourId}`, err);
  });
};

export const useGuidedTour = ({
  tourId,
  totalSteps,
  persistence,
  disableAutoTrigger = false,
}: UseGuidedTourArgs): UseGuidedTourResult => {
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  // Ref to ensure auto-trigger runs at most once per mount even if
  // strict-mode double-fires the effect.
  const autoTriggered = useRef(false);

  useEffect(() => {
    if (disableAutoTrigger || autoTriggered.current) return undefined;
    autoTriggered.current = true;

    let timer: number | null = null;
    persistence
      .isCompleted(tourId)
      .then((completed) => {
        if (completed) return;
        timer = window.setTimeout(() => setActive(true), AUTO_TRIGGER_DELAY_MS);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn(
          `[useGuidedTour] persistence read failed for ${tourId}`,
          err,
        );
      });

    return () => {
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [persistence, tourId, disableAutoTrigger]);

  const start = useCallback(() => {
    setCurrentStep(0);
    setActive(true);
  }, []);

  const close = useCallback(() => {
    setActive(false);
    reportIfFailed(persistence.markCompleted(tourId), "markCompleted", tourId);
  }, [persistence, tourId]);

  const next = useCallback(() => {
    setCurrentStep((prevStep) => {
      const isLast = prevStep >= totalSteps - 1;
      if (isLast) {
        setActive(false);
        reportIfFailed(
          persistence.markCompleted(tourId),
          "markCompleted",
          tourId,
        );
        return prevStep;
      }
      return prevStep + 1;
    });
  }, [persistence, tourId, totalSteps]);

  const prev = useCallback(() => {
    setCurrentStep((prevStep) => (prevStep > 0 ? prevStep - 1 : prevStep));
  }, []);

  const reset = useCallback(async () => {
    await persistence.reset(tourId);
    setCurrentStep(0);
    setActive(true);
  }, [persistence, tourId]);

  return { active, currentStep, start, close, next, prev, reset };
};
